import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'isomorphic-dompurify';
import PropTypes from 'prop-types';

import { Button, ButtonStyle, Form, Icon, IconSize, IconType, Input, InputStyle, Loader, LoaderSize, Message, MessageStyle, withModal } from '../Utils';

import * as s from './WireframeEditor.module.scss';

// eslint-disable-next-line import/no-unresolved
import '@excalidraw/excalidraw/index.css';

const LazyExcalidraw = React.lazy(() => import('@excalidraw/excalidraw').then((module) => ({ default: module.Excalidraw })));

const AUTOSAVE_DELAY = 1500;

const sanitizeSvg = (svg) =>
  DOMPurify.sanitize(svg, {
    USE_PROFILES: { svg: true, svgFilters: true },
    ADD_TAGS: ['foreignObject'],
  });

const getInitialData = (data) => {
  if (!data) {
    return {
      elements: [],
      appState: {
        viewBackgroundColor: '#ffffff',
      },
      files: {},
    };
  }

  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return undefined;
    }
  }

  return data;
};

const WireframeEditor = React.memo(({ attachment, canEdit, onCreate, onUpdate, onClose }) => {
  const [t] = useTranslation();
  const [name, setName] = useState(attachment?.name || t('common.untitledWireframe'));
  const [saveState, setSaveState] = useState('idle');
  const [error, setError] = useState(null);
  const [excalidrawApi, setExcalidrawApi] = useState(null);
  const [dismissedConflict, setDismissedConflict] = useState(false);
  const saveTimeout = useRef();
  const didReceiveChange = useRef(false);

  const initialData = useMemo(() => getInitialData(attachment?.data), [attachment?.data]);
  const isExisting = !!attachment?.id;

  useEffect(() => {
    setName(attachment?.name || t('common.untitledWireframe'));
    setSaveState('idle');
    setError(null);
    setDismissedConflict(false);
    didReceiveChange.current = false;
  }, [attachment?.id, attachment?.name, t]);

  useEffect(
    () => () => {
      window.clearTimeout(saveTimeout.current);
    },
    [],
  );

  const buildPayload = useCallback(async () => {
    if (!excalidrawApi) {
      return null;
    }

    const module = await import('@excalidraw/excalidraw');
    const elements = excalidrawApi.getSceneElements();
    const appState = excalidrawApi.getAppState();
    const files = excalidrawApi.getFiles();
    const data = JSON.parse(module.serializeAsJSON(elements, appState, files, 'local'));
    const svg = await module.exportToSvg({
      elements,
      appState,
      files,
      exportPadding: 12,
    });

    return {
      kind: 'wireframe',
      name: name.trim() || t('common.untitledWireframe'),
      data,
      thumbnailSvg: sanitizeSvg(svg.outerHTML),
    };
  }, [excalidrawApi, name, t]);

  const save = useCallback(
    async ({ closeAfterCreate = false } = {}) => {
      if (!canEdit || !excalidrawApi) {
        return;
      }

      setSaveState('saving');
      setError(null);

      try {
        const payload = await buildPayload();
        if (!payload) {
          return;
        }

        if (isExisting) {
          onUpdate(attachment.id, {
            ...payload,
            updatedAt: attachment.updatedAt,
          });
        } else {
          onCreate(payload);
          if (closeAfterCreate) {
            onClose();
          }
        }

        setSaveState('saved');
      } catch (nextError) {
        setError(nextError);
        setSaveState('error');
      }
    },
    [attachment?.id, attachment?.updatedAt, buildPayload, canEdit, excalidrawApi, isExisting, onClose, onCreate, onUpdate],
  );

  const handleNameChange = useCallback((event) => {
    setName(event.target.value);
  }, []);

  const handleChange = useCallback(() => {
    if (!isExisting || !canEdit) {
      return;
    }

    if (!didReceiveChange.current) {
      didReceiveChange.current = true;
      return;
    }

    setSaveState('dirty');
    window.clearTimeout(saveTimeout.current);
    saveTimeout.current = window.setTimeout(() => {
      save();
    }, AUTOSAVE_DELAY);
  }, [canEdit, isExisting, save]);

  const handleSubmit = useCallback(() => {
    save({ closeAfterCreate: !isExisting });
  }, [isExisting, save]);

  const handleApiReady = useCallback((api) => {
    setExcalidrawApi(api);
  }, []);

  const handleConflictReload = useCallback(() => {
    window.location.reload();
  }, []);

  const handleConflictDismiss = useCallback(() => {
    setDismissedConflict(true);
  }, []);

  const isConflictVisible = !!attachment?.wireframeConflict && !dismissedConflict;

  return (
    <div className={s.wrapper}>
      <Form onSubmit={handleSubmit} className={s.toolbar}>
        <Input value={name} maxLength={128} onChange={handleNameChange} placeholder={t('common.enterWireframeName')} style={InputStyle.Default} disabled={!canEdit} className={s.nameInput} />
        {canEdit && (
          <Button style={ButtonStyle.Submit} disabled={!excalidrawApi || saveState === 'saving'} className={s.saveButton}>
            {saveState === 'saving' ? t('common.saving') : t('action.save')}
          </Button>
        )}
        <Button style={ButtonStyle.Icon} title={t('common.close')} onClick={onClose} className={s.closeButton}>
          <Icon type={IconType.Close} size={IconSize.Size14} />
        </Button>
      </Form>
      {error && <Message style={MessageStyle.Error} content={t('common.wireframeSaveFailed')} className={s.message} onDismiss={() => setError(null)} />}
      {isConflictVisible && (
        <div className={s.conflict}>
          <Message style={MessageStyle.Warning} content={t('common.wireframeConflict')} className={s.conflictMessage} onDismiss={handleConflictDismiss} />
          <Button style={ButtonStyle.Default} onClick={handleConflictReload} className={s.reloadButton}>
            {t('action.reload')}
          </Button>
        </div>
      )}
      {saveState === 'dirty' && <div className={s.status}>{t('common.unsavedChanges')}</div>}
      {saveState === 'saved' && !isConflictVisible && <div className={s.status}>{t('common.saved')}</div>}
      <div className={s.canvas}>
        <Suspense
          fallback={
            <div className={s.loader}>
              <Loader size={LoaderSize.Normal} />
            </div>
          }
        >
          <LazyExcalidraw
            key={attachment?.id || 'new-wireframe'}
            initialData={initialData}
            excalidrawAPI={handleApiReady}
            viewModeEnabled={!canEdit}
            onChange={handleChange}
            UIOptions={{
              canvasActions: {
                loadScene: false,
                saveToActiveFile: false,
                export: false,
              },
            }}
          />
        </Suspense>
      </div>
    </div>
  );
});

WireframeEditor.propTypes = {
  attachment: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  canEdit: PropTypes.bool.isRequired,
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

WireframeEditor.defaultProps = {
  attachment: undefined,
};

export default withModal(WireframeEditor, {
  className: s.modal,
  hideCloseButton: true,
});
